import { Box } from "@chakra-ui/react";
import React from "react";
import Balancer from "react-wrap-balancer";
import {
  AlertIcon,
  CalendarIcon,
  CornerDownRightIcon,
  GlobeIcon,
  HeartIcon,
  LightningIcon,
  MinusCircleIcon,
  MobilePhoneIcon,
  MoneyIcon,
  PeopleIcon,
  PersonWithCheckIcon,
  PinIcon,
  RefreshIcon,
  SearchIcon,
  SettingsIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  TargetIcon,
  TrendIcon,
} from "ui/icons";
import hero from "../../../../public/assets/cdp-industries/heros/d2c.png";
import { dataBreachStat, personalizedExperiencesStat, revenueReferenceCopy, type CdpIndustryConfig } from "./shared";

export const d2cCdpConfig: CdpIndustryConfig = {
  icon: PeopleIcon,
  name: "D2C",
  titleCaseName: "D2C",
  pluralNoun: "brands",
  color: "blue",
  seoCopy:
    "Leverage all your D2C data, including customers, orders, products, subscriptions, shipping records, and returns.",
  heroImage: {
    src: hero,
    alt: "A photograph of a person stretching with a list of example data tables overlayed. The data tables included are for orders, shipping records, customers, subscriptions, and returns.",
  },
  adoptingCdpsDescription: (
    <>
      D2C customers are overwhelmed with choices because they’re inundated with options for nearly every product
      category. Customers crave relevancy and authenticity. {revenueReferenceCopy}
    </>
  ),
  featuredOutcomes: [
    {
      icon: MoneyIcon,
      color: "yellow",
      title: "Grow AOV & LTV",
    },
    {
      icon: TrendIcon,
      color: "green",
      title: "Increase website traffic",
    },
    {
      icon: HeartIcon,
      color: "red",
      title: "Build loyalty",
    },
  ],
  dataStats: [
    {
      title: "14%",
      description: (
        <Balancer>
          Global ecommerce sales
          <Box as="br" display={{ base: "block", lg: "none" }} /> are attributed to D2C
        </Balancer>
      ),
      href: "https://nielseniq.com/global/en/insights/commentary/2024/the-evolving-direct-to-consumer-dtc-journey-2024/",
    },
    personalizedExperiencesStat,
    dataBreachStat,
  ],
  personalizationCards: [
    {
      title:
        "Warby Parker uses advanced AI to build highly curated customer journeys and drive users towards specific outcomes",
      href: "https://cotera.co/blog/in-focus-how-warby-parker-redefines-cx-through-digital-physical-integration",
    },
    {
      title:
        "True Classic runs thousands of experiments to optimize creative, messaging, targeting, and post-purchase offers",
      href: "https://www.peelinsights.com/post/peel-announces-partnership-with-true-classic",
    },
    {
      title:
        "Casper launched AI agent to provide personalized, real-time responses and recommendations to customers based on their preferences and needs",
      href: "https://marketerintheloop.com/p/casper-s-ai-powered-customer-experience-story",
    },
  ],
  useCases: {
    performanceMarketing: [
      // First row
      {
        icon: PersonWithCheckIcon,
        title: "Acquisition campaigns",
        description:
          "Drive new customer acquisition using demographic and interest data to target potential customers and lookalike audiences with first-time discounts, increasing your customer base while reducing CAC.",
      },
      {
        icon: MinusCircleIcon,
        title: "Suppression campaigns",
        description:
          "Reduce wasted ad spend by suppressing frequent buyers from acquisition campaigns using CRM data, reallocating budget toward untapped or lapsed customers.",
      },
      {
        icon: TargetIcon,
        title: "Retargeting campaigns",
        description:
          "Recover abandoned carts by serving dynamic ads featuring cart items with personalized offers, driving incremental conversions and recovering lost revenue.",
      },
      // Second row
      {
        icon: MoneyIcon,
        title: "Cross-sell campaigns",
        description:
          "Increase AOV by promoting complementary products using purchase history to serve targeted ads, driving incremental revenue and deepening customer engagement.",
      },
      {
        icon: SettingsIcon,
        title: "Conversion APIs",
        description:
          "Share online and offline conversion data with ad platforms, enabling algorithms to better identify and target users with a higher likelihood of converting to improve campaign performance.",
      },
      {
        icon: GlobeIcon,
        title: "Media networks",
        description:
          "Monetize consumer insights by offering anonymized audience segments to brand partners for targeted advertising, generating incremental revenue through premium audience data sales.",
      },
    ],
    lifecycleMarketing: [
      // First row
      {
        icon: MoneyIcon,
        title: "Upsell & cross-sell campaigns",
        description:
          "Use purchase history to recommend premium versions, accessories, or bundles, increasing AOV and LTV.",
      },
      {
        icon: RefreshIcon,
        title: "Reactivation campaigns",
        description: "Re-engage inactive customers with exclusive discounts or personalized email campaigns.",
      },
      {
        icon: AlertIcon,
        title: "Replenishment reminders",
        description: "Send automated restock reminders for consumable products, driving recurring revenue.",
      },
      // Second row
      {
        icon: CalendarIcon,
        title: "Milestone rewards campaigns",
        description: "Reward repeat customers with exclusive discounts or loyalty points to strengthen brand loyalty.",
      },
      {
        icon: PinIcon,
        title: "Localized promotions",
        description: "Promote free shipping or exclusive in-store events in key markets to boost sales.",
      },
      {
        icon: HeartIcon,
        title: "Loyalty program engagement",
        description:
          "Offer early access to sales, double points weekends, or special perks to drive loyalty engagement.",
      },
    ],
    onsitePersonalization: [
      // First row
      {
        icon: LightningIcon,
        title: "Dynamic home page content",
        description:
          "Display personalized banners featuring trending categories or products based on browsing and purchase history to increase engagement and drive conversions.",
      },
      {
        icon: MobilePhoneIcon,
        title: "Dynamic app experiences",
        description:
          "Customize app dashboards by highlighting repeat purchases, favorite categories, or loyalty rewards to simplify navigation and boost engagement.",
      },
      {
        icon: SearchIcon,
        title: "Dynamic search results",
        description:
          "Tailor search results to prioritize in-stock items, trending products, or frequently searched categories to improve product discovery and search-to-purchase conversion rates.",
      },
      // Second row
      {
        icon: ShoppingBagIcon,
        title: "Recommended products",
        description:
          "Suggest complementary or frequently bought together items based on browsing and purchase history to increase average order value (AOV).",
      },
      {
        icon: CornerDownRightIcon,
        title: "Next best action",
        description:
          "Encourage valuable actions like signing up for newsletters, joining loyalty programs, or completing abandoned wish lists to drive engagement and repeat purchases.",
      },
      {
        icon: ShoppingCartIcon,
        title: "Proactive cart nudges",
        description:
          "Recover abandoned carts by sending urgency-based notifications, such as low-stock alerts or limited-time discounts, to improve conversion rates.",
      },
    ],
  },
  considerations: {
    dataModeling: {
      traditional:
        "Uses predefined models that may not fully capture subscription patterns or product personalization needs",
      composable: "Supports tailored models to handle subscription data, churn signals, and purchase behaviors",
    },
    audienceManagement: {
      traditional:
        "Supports broad segmentation but struggles with categories like subscription tiers product preferences or engagement trends",
      composable: "Enables granular segmentation using factors like churn risks, purchase frequency, and loyalty tiers",
    },
    customerJourneys: {
      traditional: "Provides standard templates for campaigns like product launches or email promotions",
      composable:
        "Powers fully adaptable journeys for events like subscription renewals, first-time buyers, and cross-sell campaigns",
    },
    identityResolution: {
      traditional:
        "Relies on out-of-the-box algorithms that may not unify profiles across e-commerce platforms and loyalty programs",
      composable:
        "Supports custom algorithms to unify customer profiles from subscriptions, loyalty, and e-commerce systems",
    },
  },
  audienceOverlap: {
    title: "High AOV shoppers who haven’t purchased",
    firstCategoryLabel: "Cart abandoners (AOV > $100)",
    secondCategoryLabel: "SMS & email subscribers",
  },
};
